import { useMemo, useState, useId, Fragment } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  RefreshCw,
  Loader2,
  X,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  Plug,
  ExternalLink,
  CalendarDays,
} from 'lucide-react';
import { format, addDays, parseISO } from 'date-fns';
import { cn } from '@/lib/utils';
import { formatMoney } from '@/lib/money';
import { showToast } from '@/lib/toast';
import { extractErrorMessage } from '@/lib/error';
import { EmptyState } from '@/components/ui/EmptyState';
import { ParityBadge, parityStateLabel } from '@/components/channels/parityState';
import {
  useSyncSnapshot,
  useChangeLog,
  useCellDetail,
  useVerifySync,
  usePushSyncCell,
  useStatusPill,
} from '@/hooks/api/useChannelSync';
import {
  connectedOtasFromSnapshot,
  resolvePrimaryOta,
  getPrimaryOtaPref,
  setPrimaryOtaPref,
  type SyncCell,
  type SyncMetric,
  type SyncSnapshot,
} from '@/services/api/channelSync.api';

// ─── Local helpers ──────────────────────────────────────────────────────────

function fmtRelative(iso: string | null | undefined): string {
  if (!iso) return 'Never';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function cellKey(roomId: string, otaId: string, date: string): string {
  return `${roomId}\u0000${otaId}\u0000${date}`;
}

/** Metric-aware display value for a cell. */
function cellValue(cell: SyncCell | undefined, metric: SyncMetric): string {
  if (!cell) return '—';
  if (metric === 'availability') {
    if (cell.closed) return 'Closed';
    return cell.otaLoadedFree != null ? `${cell.otaLoadedFree} free` : '—';
  }
  return cell.otaLoadedRate != null ? formatMoney(cell.otaLoadedRate) : '—';
}

// ─── Health strip ─────────────────────────────────────────────────────────

function SyncHealthStrip({ summary }: { summary: SyncSnapshot['summary'] }) {
  const items: Array<{ label: string; n: number; tone: string }> = [
    { label: 'changed on AxisRooms', n: summary.externalChange, tone: 'text-blue-700' },
    { label: 'push unconfirmed', n: summary.pushUnconfirmed, tone: 'text-amber-800' },
    { label: 'rooms closed', n: summary.closed, tone: 'text-slate-700' },
    { label: 'OTA divergences', n: summary.divergence, tone: 'text-purple-700' },
    { label: 'sync errors', n: summary.syncErrors, tone: 'text-gray-500' },
  ];
  return (
    <div
      className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm"
      role="status"
      aria-live="polite"
    >
      {items.map((it, i) => (
        <span key={it.label} className="flex items-center gap-1.5">
          {i > 0 && <span className="text-gray-300" aria-hidden="true">·</span>}
          <span className={cn('font-semibold', it.n > 0 ? it.tone : 'text-gray-400')}>{it.n}</span>
          <span className="text-gray-500">{it.label}</span>
        </span>
      ))}
    </div>
  );
}

// ─── Primary OTA selector (Q-A) ─────────────────────────────────────────────

function PrimaryOtaSelector({
  propertyId,
  otas,
  value,
  onChange,
}: {
  propertyId: string;
  otas: Array<{ otaId: string; otaName: string }>;
  value: string | null;
  onChange: (otaId: string) => void;
}) {
  const id = useId();
  const pref = getPrimaryOtaPref(propertyId);
  if (otas.length === 0) return null;
  return (
    <div className="flex items-center gap-1.5">
      <label htmlFor={id} className="text-xs text-gray-500 whitespace-nowrap">
        Calendar mirror:
      </label>
      <div className="relative">
        <select
          id={id}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className="appearance-none rounded-lg border border-gray-300 bg-white pl-2.5 pr-7 py-1.5 text-xs font-medium text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          {otas.map((o) => (
            <option key={o.otaId} value={o.otaId}>
              {o.otaName}
              {pref == null && o.otaId === value ? ' (default)' : ''}
            </option>
          ))}
        </select>
        <ChevronDown size={13} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" aria-hidden="true" />
      </div>
    </div>
  );
}

// ─── Edit & push dialog (Flow 1.5) ──────────────────────────────────────────

function EditAndPushDialog({
  propertyId,
  cell,
  metric,
  roomLabel,
  onClose,
}: {
  propertyId: string;
  cell: SyncCell;
  metric: SyncMetric;
  roomLabel: string;
  onClose: () => void;
}) {
  const titleId = useId();
  const push = usePushSyncCell();
  const [rate, setRate] = useState(cell.otaLoadedRate != null ? String(cell.otaLoadedRate) : '');
  const [open, setOpen] = useState(!cell.closed);
  // Escalate to the red conflict variant when AxisRooms changed this cell since
  // our last sync (Q-G). We can only pre-check via the current parity state;
  // the push response's `wouldOverwriteExternalChange` corroborates it.
  const conflict = cell.parityState === 'external_change';

  const from = cell.date;
  const to = format(addDays(parseISO(cell.date), 1), 'yyyy-MM-dd');
  const newRate = parseFloat(rate);
  const rateValid = metric !== 'rate' || (!Number.isNaN(newRate) && newRate >= 0);
  const dirty =
    metric === 'rate'
      ? rateValid && (cell.otaLoadedRate == null || Math.abs(newRate - cell.otaLoadedRate) > 0.001)
      : open === cell.closed; // toggled

  function submit() {
    if (!rateValid) return;
    const value = metric === 'rate' ? newRate : open ? Math.max(1, cell.otaLoadedFree ?? 1) : 0;
    push.mutate(
      { propertyId, unitId: cell.unitId, rateplanId: cell.rateplanId, metric, from, to, value },
      {
        onSuccess: (res) => {
          showToast(
            res.wouldOverwriteExternalChange
              ? 'Pushed — overwrote an AxisRooms change. Pending confirmation.'
              : 'Pushed to AxisRooms — pending confirmation.',
          );
          onClose();
        },
        onError: (err) => showToast(extractErrorMessage(err), 'error'),
      },
    );
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-labelledby={titleId}>
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <h2 id={titleId} className="text-base font-semibold text-gray-900">Edit &amp; push to AxisRooms</h2>
          <button type="button" onClick={onClose} className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="space-y-4 px-5 py-5">
          <p className="text-sm text-gray-600">
            {roomLabel} · {cell.otaName ?? cell.otaId} · {format(parseISO(cell.date), 'MMM d, yyyy')}
          </p>

          {metric === 'rate' ? (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">New rate (USD)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500"
                autoFocus
              />
              <p className="mt-1 text-xs text-gray-400">
                Current on AxisRooms: {cell.otaLoadedRate != null ? formatMoney(cell.otaLoadedRate) : '—'}
              </p>
            </div>
          ) : (
            <div>
              <span className="block text-xs font-medium text-gray-600 mb-1">Availability</span>
              <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
                <button type="button" onClick={() => setOpen(true)} className={cn('px-3 py-1.5 text-sm font-medium', open ? 'bg-primary-600 text-white' : 'bg-white text-gray-600')}>Open</button>
                <button type="button" onClick={() => setOpen(false)} className={cn('px-3 py-1.5 text-sm font-medium', !open ? 'bg-slate-600 text-white' : 'bg-white text-gray-600')}>Close</button>
              </div>
              <p className="mt-1 text-xs text-gray-400">Currently {cell.closed ? 'closed' : `open (${cell.otaLoadedFree ?? '—'} free)`}.</p>
            </div>
          )}

          {conflict ? (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
              <AlertTriangle size={15} className="mt-0.5 shrink-0" aria-hidden="true" />
              <span>
                AxisRooms changed this value since the last sync. Pushing will <strong>overwrite that change</strong> and make your value the new master.
              </span>
            </div>
          ) : (
            <p className="text-sm text-gray-500">
              This pushes to AxisRooms and becomes the new master value. The next pull confirms it landed.
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-100 px-5 py-4">
          <button type="button" onClick={onClose} disabled={push.isPending} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50">Cancel</button>
          <button
            type="button"
            onClick={submit}
            disabled={push.isPending || !rateValid || !dirty}
            className={cn(
              'inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white',
              push.isPending || !rateValid || !dirty
                ? 'bg-gray-300 cursor-not-allowed'
                : conflict
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-primary-600 hover:bg-primary-700',
            )}
          >
            {push.isPending && <Loader2 size={14} className="animate-spin" />}
            {conflict ? 'Overwrite & push' : 'Confirm push'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Per-cell drill popover (Flow 1.4) ──────────────────────────────────────

function SyncCellPopover({
  propertyId,
  cell,
  roomLabel,
  onEdit,
  onClose,
}: {
  propertyId: string;
  cell: SyncCell;
  roomLabel: string;
  onEdit: () => void;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const { data, isLoading } = useCellDetail({
    propertyId,
    unitId: cell.unitId,
    rateplanId: cell.rateplanId,
    date: cell.date,
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-label="Sync cell detail" onClick={onClose}>
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <div>
            <h2 className="text-base font-semibold text-gray-900">{roomLabel}</h2>
            <p className="text-xs text-gray-500">{cell.otaName ?? cell.otaId} · {format(parseISO(cell.date), 'MMM d, yyyy')}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600" aria-label="Close"><X size={18} /></button>
        </div>

        <div className="px-5 py-4 space-y-3 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-gray-500">State</span>
            <ParityBadge state={cell.parityState} size="sm" />
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            <div><span className="text-gray-500">Live AxisRooms rate</span><p className="font-medium text-gray-900">{cell.otaLoadedRate != null ? formatMoney(cell.otaLoadedRate) : '—'}</p></div>
            <div><span className="text-gray-500">PMS last-known</span><p className="font-medium text-gray-900">{cell.pmsLastKnownRate != null ? formatMoney(cell.pmsLastKnownRate) : '—'}</p></div>
            <div><span className="text-gray-500">Availability</span><p className="font-medium text-gray-900">{cell.closed ? 'Closed' : cell.otaLoadedFree != null ? `${cell.otaLoadedFree} free` : '—'}</p></div>
            <div><span className="text-gray-500">Rate plan</span><p className="font-medium text-gray-900">{cell.rateplanId}</p></div>
            <div><span className="text-gray-500">Last synced</span><p className="font-medium text-gray-900">{fmtRelative(cell.lastSyncedAt)}</p></div>
            <div><span className="text-gray-500">Verify</span><p className="font-medium text-gray-900">{cell.verifyStatus === 'couldnt_verify' ? "Couldn't verify" : 'OK'}</p></div>
          </div>

          {cell.verifyStatus === 'couldnt_verify' && (
            <p className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 text-xs text-gray-500">
              Showing the last good value — couldn&apos;t verify at the last pull. Use Sync now to retry.
            </p>
          )}

          <div>
            <span className="block text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1.5">Recent history</span>
            {isLoading ? (
              <div className="flex items-center gap-2 py-2 text-xs text-gray-400"><Loader2 size={13} className="animate-spin" /> Loading…</div>
            ) : (data?.history.length ?? 0) === 0 ? (
              <p className="text-xs text-gray-400">No recorded changes.</p>
            ) : (
              <ul className="space-y-1.5 max-h-40 overflow-y-auto">
                {data!.history.map((h) => (
                  <li key={h.id} className="text-xs text-gray-600">
                    <span className="font-medium capitalize">{h.eventType.replace(/_/g, ' ')}</span>
                    {h.oldValue != null && h.newValue != null && (
                      <> · {h.metric === 'rate' ? formatMoney(Number(h.oldValue)) : h.oldValue} → {h.metric === 'rate' ? formatMoney(Number(h.newValue)) : h.newValue}</>
                    )}
                    <span className="text-gray-400"> · {fmtRelative(h.detectedAt)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="flex justify-between gap-2 border-t border-gray-100 px-5 py-4">
          <button
            type="button"
            onClick={() => navigate(`/rate-calendar?unit=${cell.unitId}&date=${cell.date}`)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-50"
          >
            <CalendarDays size={14} aria-hidden="true" /> View in Rate Calendar
          </button>
          <button
            type="button"
            onClick={onEdit}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
          >
            Edit &amp; push
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Change log (Flow 1.3) ──────────────────────────────────────────────────

function SyncChangeLog({ propertyId }: { propertyId: string }) {
  const { data, isLoading } = useChangeLog({ propertyId, limit: 25 });
  const rows = data?.data ?? [];

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100">
        <h2 className="text-base font-semibold text-gray-800">Change log</h2>
        <p className="text-xs text-gray-400 mt-0.5">Changes the pull detected on AxisRooms · your pushes tagged (your edit)</p>
      </div>
      {isLoading ? (
        <div className="flex items-center gap-2 px-5 py-6 text-sm text-gray-500"><Loader2 size={16} className="animate-spin" /> Loading…</div>
      ) : rows.length === 0 ? (
        <p className="px-5 py-6 text-sm text-gray-500">No changes recorded yet.</p>
      ) : (
        <ul className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
          {rows.map((r) => {
            const isPush = r.eventType === 'push' || r.eventType === 'push_confirmed' || r.eventType === 'push_unconfirmed';
            const fmtVal = (v: string | null) => (v == null ? '—' : r.metric === 'rate' ? formatMoney(Number(v)) : v);
            return (
              <li key={r.id} className="px-5 py-2.5 text-sm flex items-center justify-between gap-3">
                <span className="text-gray-700">
                  <span className="font-medium">{r.otaName ?? r.otaId}</span>
                  <span className="text-gray-400"> · {format(parseISO(r.stayDate), 'MMM d')} · </span>
                  <span className="capitalize">{r.eventType.replace(/_/g, ' ')}</span>
                  {r.oldValue != null && r.newValue != null && (
                    <span className="text-gray-500"> · {fmtVal(r.oldValue)} → {fmtVal(r.newValue)}</span>
                  )}
                  {isPush && <span className="ml-1.5 text-[10px] font-medium text-primary-600">(your edit)</span>}
                </span>
                <span className="shrink-0 text-xs text-gray-400">{fmtRelative(r.detectedAt)}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ─── Sync grid (Flow 1.2) ───────────────────────────────────────────────────

interface RoomGroup {
  roomId: string;
  unitId: string;
  label: string;
  otas: string[]; // ota ids present
  otaNames: Map<string, string>;
}

function SyncGrid({
  cells,
  dates,
  rooms,
  metric,
  primaryOta,
  onOpenCell,
}: {
  cells: Map<string, SyncCell>;
  dates: string[];
  rooms: RoomGroup[];
  metric: SyncMetric;
  primaryOta: string | null;
  onOpenCell: (cell: SyncCell, roomLabel: string) => void;
}) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  function pickCell(roomId: string, otaId: string, date: string): SyncCell | undefined {
    return cells.get(cellKey(roomId, otaId, date));
  }

  function renderCellButton(roomId: string, otaId: string, date: string, label: string) {
    const cell = pickCell(roomId, otaId, date);
    const badge = cell && cell.parityState !== 'synced' && cell.parityState !== 'unknown';
    return (
      <button
        type="button"
        disabled={!cell}
        onClick={() => cell && onOpenCell(cell, label)}
        className={cn(
          'w-full min-w-[84px] rounded-md px-2 py-1.5 text-xs font-semibold transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
          !cell
            ? 'text-gray-300 cursor-default'
            : cell.closed
              ? 'bg-slate-100 text-slate-500 line-through hover:bg-slate-200'
              : 'bg-gray-50 text-gray-800 hover:bg-gray-100',
        )}
        aria-label={
          cell
            ? `${label}, ${cell.otaName ?? cell.otaId}, ${format(parseISO(date), 'MMM d')}: ${cellValue(cell, metric)}, ${parityStateLabel(cell.parityState)}`
            : undefined
        }
      >
        <span className="block">{cellValue(cell, metric)}</span>
        {badge && <span className="mt-1 flex justify-center"><ParityBadge state={cell!.parityState} size="dot" /></span>}
      </button>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full" role="grid" aria-label="AxisRooms sync grid">
          <thead>
            <tr className="bg-gray-50">
              <th scope="col" className="sticky left-0 z-10 bg-gray-50 px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide min-w-[190px] border-r border-gray-200">Room</th>
              {dates.map((d) => (
                <th key={d} scope="col" className="px-2 py-3 text-center text-xs font-semibold text-gray-500 min-w-[90px] border-r border-gray-100">
                  <div>{format(parseISO(d), 'EEE')}</div>
                  <div className="font-bold text-[11px]">{format(parseISO(d), 'MMM d')}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rooms.map((room) => {
              const isExpanded = expanded.has(room.roomId);
              const primary = primaryOta && room.otas.includes(primaryOta) ? primaryOta : room.otas[0];
              const subOtas = room.otas.filter((o) => o !== primary);
              return (
                <Fragment key={room.roomId}>
                  <tr className="hover:bg-gray-50/50">
                    <td className="sticky left-0 z-10 bg-white px-4 py-2 border-r border-gray-200">
                      <div className="flex items-center gap-1.5">
                        {subOtas.length > 0 ? (
                          <button
                            type="button"
                            onClick={() => setExpanded((prev) => { const n = new Set(prev); if (n.has(room.roomId)) n.delete(room.roomId); else n.add(room.roomId); return n; })}
                            className="text-gray-400 hover:text-gray-600"
                            aria-expanded={isExpanded}
                            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} OTAs for ${room.label}`}
                          >
                            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          </button>
                        ) : (
                          <span className="w-3.5" />
                        )}
                        <div>
                          <div className="font-medium text-sm text-gray-900">{room.label}</div>
                          <div className="text-xs text-gray-400">{primary ? room.otaNames.get(primary) ?? primary : '—'}{subOtas.length > 0 ? ` +${subOtas.length}` : ''}</div>
                        </div>
                      </div>
                    </td>
                    {dates.map((d) => (
                      <td key={d} className="px-1 py-1 text-center border-r border-gray-100">
                        {primary ? renderCellButton(room.roomId, primary, d, room.label) : <span className="text-gray-300 text-xs">—</span>}
                      </td>
                    ))}
                  </tr>
                  {isExpanded &&
                    subOtas.map((otaId) => (
                      <tr key={`${room.roomId}:${otaId}`} className="bg-gray-50/40">
                        <td className="sticky left-0 z-10 bg-gray-50/40 pl-9 pr-4 py-1.5 border-r border-gray-200 text-xs text-gray-500">
                          {room.otaNames.get(otaId) ?? otaId}
                        </td>
                        {dates.map((d) => (
                          <td key={d} className="px-1 py-1 text-center border-r border-gray-100">
                            {renderCellButton(room.roomId, otaId, d, room.label)}
                          </td>
                        ))}
                      </tr>
                    ))}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Panel ──────────────────────────────────────────────────────────────────

export function AxisRoomsSyncPanel({
  propertyId,
  unitNames,
  hasAxisRooms,
  mappedRooms,
}: {
  propertyId: string;
  unitNames: Map<string, string>;
  hasAxisRooms: boolean;
  mappedRooms: number;
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const roomFilter = searchParams.get('room') ?? undefined;
  const otaFilter = searchParams.get('ota') ?? undefined;

  const [metric, setMetric] = useState<SyncMetric>('rate');
  const [primaryOta, setPrimaryOta] = useState<string | null>(null);
  const [openCell, setOpenCell] = useState<{ cell: SyncCell; roomLabel: string } | null>(null);
  const [editCell, setEditCell] = useState<{ cell: SyncCell; roomLabel: string } | null>(null);

  const snapshotQuery = useSyncSnapshot(
    { propertyId, metric, ...(roomFilter ? { roomId: roomFilter } : {}), ...(otaFilter ? { otaId: otaFilter } : {}) },
    !!propertyId,
  );
  const statusPill = useStatusPill(propertyId, !!propertyId);
  const verify = useVerifySync();

  const snapshot = snapshotQuery.data;
  const cells = useMemo(() => snapshot?.cells ?? [], [snapshot]);

  const otas = useMemo(() => connectedOtasFromSnapshot(cells), [cells]);
  const effectivePrimary = primaryOta ?? resolvePrimaryOta(propertyId, cells);

  // Build the room × date grid model.
  const { dates, rooms, cellMap } = useMemo(() => {
    const dateSet = new Set<string>();
    const roomMap = new Map<string, RoomGroup>();
    const map = new Map<string, SyncCell>();
    for (const c of cells) {
      dateSet.add(c.date);
      let room = roomMap.get(c.externalListingId);
      if (!room) {
        room = {
          roomId: c.externalListingId,
          unitId: c.unitId,
          label: unitNames.get(c.unitId) ?? c.externalListingId,
          otas: [],
          otaNames: new Map(),
        };
        roomMap.set(c.externalListingId, room);
      }
      if (!room.otas.includes(c.otaId)) room.otas.push(c.otaId);
      room.otaNames.set(c.otaId, c.otaName ?? c.otaId);
      // One cell per (room, ota, date): prefer the smallest rateplan deterministically.
      const k = cellKey(c.externalListingId, c.otaId, c.date);
      const existing = map.get(k);
      if (!existing || c.rateplanId.localeCompare(existing.rateplanId) < 0) map.set(k, c);
    }
    for (const room of roomMap.values()) room.otas.sort((a, b) => a.localeCompare(b));
    return {
      dates: [...dateSet].sort(),
      rooms: [...roomMap.values()].sort((a, b) => a.label.localeCompare(b.label)),
      cellMap: map,
    };
  }, [cells, unitNames]);

  function runVerify() {
    verify.mutate(
      { propertyId, ...(roomFilter ? { roomId: roomFilter } : {}), ...(otaFilter ? { otaId: otaFilter } : {}) },
      {
        onSuccess: (res) => {
          const failed = res.failedOtas.length;
          showToast(
            failed > 0
              ? `Synced — ${res.summary.externalChange} changed, ${failed} OTA(s) couldn't verify`
              : `Synced — ${res.summary.externalChange} changed on AxisRooms`,
          );
        },
        onError: (err) => showToast(extractErrorMessage(err), 'error'),
      },
    );
  }

  // ── Guards / empty states ──────────────────────────────────────────────
  if (!propertyId) {
    return <EmptyState icon={Plug} title="Select a property" description="Choose a single property to view its AxisRooms sync." />;
  }
  if (!hasAxisRooms) {
    return (
      <EmptyState
        icon={Plug}
        title="Connect AxisRooms to sync"
        description="This property has no AxisRooms connection yet."
        actionLabel="Connect Channel"
        onAction={() => navigate('/channels/connect')}
      />
    );
  }
  if (mappedRooms === 0) {
    return (
      <EmptyState
        icon={AlertTriangle}
        title="No rooms mapped yet"
        description="Map your rooms to AxisRooms room IDs before you can read back rates and availability."
        actionLabel="Set up room mapping"
        onAction={() => navigate('/channels/connect')}
      />
    );
  }

  const stale = statusPill.data?.stale ?? false;
  const lastSynced = snapshot?.lastSyncedAt ?? statusPill.data?.lastSyncedAt ?? null;
  const verifyResult = verify.data;
  const partialFailed = verifyResult?.failedOtas ?? [];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900">AxisRooms sync — live values</h2>
          <p className={cn('text-xs mt-0.5', stale ? 'text-amber-600' : 'text-gray-400')}>
            Synced {fmtRelative(lastSynced)}{stale ? ' — may be stale' : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <PrimaryOtaSelector
            propertyId={propertyId}
            otas={otas}
            value={effectivePrimary}
            onChange={(otaId) => { setPrimaryOtaPref(propertyId, otaId); setPrimaryOta(otaId); }}
          />
          {/* Metric toggle */}
          <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden" role="group" aria-label="Metric">
            {(['rate', 'availability'] as SyncMetric[]).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMetric(m)}
                aria-pressed={metric === m}
                className={cn('px-3 py-1.5 text-xs font-medium capitalize', metric === m ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50')}
              >
                {m}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={runVerify}
            disabled={verify.isPending}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
              verify.isPending ? 'border-gray-200 text-gray-300 cursor-not-allowed' : 'border-primary-300 text-primary-600 hover:bg-primary-50',
            )}
          >
            <RefreshCw size={13} className={verify.isPending ? 'animate-spin' : ''} aria-hidden="true" />
            {verify.isPending ? 'Syncing…' : 'Sync now'}
          </button>
        </div>
      </div>

      {/* Non-blocking couldn't-verify banner */}
      {partialFailed.length > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800" role="status">
          <AlertTriangle size={15} className="mt-0.5 shrink-0" aria-hidden="true" />
          <span>Couldn&apos;t sync {partialFailed.join(', ')} — showing last known values. <button type="button" onClick={runVerify} className="font-semibold underline">Retry</button></span>
        </div>
      )}

      {/* Health strip */}
      {snapshot && <SyncHealthStrip summary={snapshot.summary} />}

      {/* Grid */}
      {snapshotQuery.isLoading ? (
        <div className="flex items-center justify-center py-16 bg-white rounded-xl border border-gray-200">
          <Loader2 size={22} className="animate-spin text-primary-600" /><span className="ml-2 text-sm text-gray-500">Loading sync grid…</span>
        </div>
      ) : snapshotQuery.isError ? (
        <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <AlertTriangle size={15} className="mt-0.5 shrink-0" aria-hidden="true" />
          Couldn&apos;t reach the sync snapshot. <button type="button" onClick={() => void snapshotQuery.refetch()} className="font-semibold underline">Retry</button>
        </div>
      ) : cells.length === 0 ? (
        <EmptyState
          icon={RefreshCw}
          title="Not synced yet"
          description="No AxisRooms values mirrored for this property yet. Run a sync to pull the current rates and availability."
          actionLabel="Sync now"
          onAction={runVerify}
        />
      ) : (
        <SyncGrid
          cells={cellMap}
          dates={dates}
          rooms={rooms}
          metric={metric}
          primaryOta={effectivePrimary}
          onOpenCell={(cell, roomLabel) => setOpenCell({ cell, roomLabel })}
        />
      )}

      {/* Deep-link to full report */}
      <div>
        <button
          type="button"
          onClick={() => navigate('/rate-parity')}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700"
        >
          Open full parity report <ExternalLink size={13} aria-hidden="true" />
        </button>
      </div>

      {/* Change log */}
      <SyncChangeLog propertyId={propertyId} />

      {/* Overlays */}
      {openCell && (
        <SyncCellPopover
          propertyId={propertyId}
          cell={openCell.cell}
          roomLabel={openCell.roomLabel}
          onClose={() => setOpenCell(null)}
          onEdit={() => { setEditCell(openCell); setOpenCell(null); }}
        />
      )}
      {editCell && (
        <EditAndPushDialog
          propertyId={propertyId}
          cell={editCell.cell}
          metric={metric}
          roomLabel={editCell.roomLabel}
          onClose={() => setEditCell(null)}
        />
      )}
    </div>
  );
}
